import React from "react";
import { useTranslation } from "react-i18next";
import { StyledModal, StyledLabel, CheckboxIcon } from "@aureskonnect/react-ui";
import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useSnapshot } from "valtio";
import { mutate } from "swr";
import { toast } from "react-toastify";
import { uuid } from "uuidv4";

import {
    store as generalSettingStore,
    setIsValidateArchiveOpened,
    setIsValidateShopArchiveOpened,
    setIsLoading,
    setKey,
} from "./store";
import { store } from "@components/VerticalLayout/store";
import { generalConfigStore, setItemsDisable } from "@store";

import { capitalize } from "@helpers/general";

import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import { languages } from "@constants/index";
import ErrorToast from "@components/Common/ErrorTost";

type ConfirmationArchiveLanguageType = {
    selectedRows: any;
    setLimit: Function;
};

export function ConfirmationArchiveLanguage({
    selectedRows,
    setLimit,
}: ConfirmationArchiveLanguageType) {
    const { t } = useTranslation();
    const {
        isValidateArchiveOpened,
        isValidateShopArchiveOpened,
    } = useSnapshot(generalSettingStore);
    const { userID, franchiseID, shopID, operatorID } = useSnapshot(store);
    const { itemsDisable } = useSnapshot(generalConfigStore);

    const [error, setError] = React.useState<boolean>(false);
    const uuidUser = localStorage.getItem("uuidUser");

    async function isLanguageConsumed(shopId: string, id: string) {
        toast.dismiss();
        let isLanguageUsed = false;
        const url = `${process.env.REACT_APP_API_V2_URL}/settings/consumedSettingGeneral?shopId=${shopId}&franchiseId=${franchiseID}&type=languages`;
        mutate(
            url,
            await fetch(url, {
                method: "GET",
                headers: { "Content-Type": "application/json" },
            })
                .then((response) => response.json())
                .then((result) => {
                    if (result.data.some((el: any) => el.id === id)) {
                        isLanguageUsed = true;
                    }
                })
                .catch((error) => {
                    toast.error(`${t("There's an error")!}`, {
                        position: toast.POSITION.TOP_CENTER,
                        autoClose: 2000,
                        theme: "colored",
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    });
                })
        );
        return isLanguageUsed;
    }
    async function archiveFranchiseLanguage() {
        setIsLoading(true);
        toast.dismiss();
        setLimit(3);
        let localData: any = {};
        let isSomeConsumed = false;
        let isFavorite = false;
        for (let el of selectedRows) {
            const id = el.Id;
            if (el.isFavorite === true) {
                isFavorite = true;
            } else {
                let isConsumed: any = await isLanguageConsumed(el.shopId, id);
                if (isConsumed === true) {
                    isSomeConsumed = true;
                } else {
                    Object.assign(localData, {
                        [id]: {
                            id: id,
                            language: languages.find(
                                (lg: any) => lg.value === el.abbreviation
                            )?.label,
                            display_name: el.display_name,
                            country: el.country,
                            urlImage: el.url_image,
                            abbreviation: el.abbreviation,
                            isArchived: true,
                            shopId: el.shopId,
                            isFavorite: el.isFavorite,
                            isActive: false,
                            advancedDisplayName: el.advancedDisplayName,
                        },
                    });
                }
            }
        }

        if (Object.keys(localData).length !== 0) {
            let dataLanguage = {
                userId: userID,
                shopId: [shopID],
                franchiseId: franchiseID,
                operatorId: operatorID,
                uuidUser: uuidUser,
                isShop: false,
                data: localData,
            };
            const data = new FormData();
            data.append("dataLanguage", JSON.stringify(dataLanguage));
            data.append("image", "");
            const apiUrlAdd = `${process.env.REACT_APP_API_V2_URL}/settings/general/language`;
            try {
                mutate(
                    apiUrlAdd,
                    await fetch(apiUrlAdd, {
                        method: "POST",
                        body: data,
                    })
                        .then((response) => response.json())
                        .then((data) => {
                            if (data.error) {
                                throw Error("error");
                            }
                            toast.success(
                                `${t(
                                    "The language was successfully archived"
                                )}`,
                                {
                                    position: toast.POSITION.TOP_CENTER,
                                    theme: "colored",
                                    icon: (
                                        <CheckboxIcon
                                            height={25}
                                            width={25}
                                            fill="white"
                                        />
                                    ),
                                }
                            );
                            setIsLoading(false);
                            setKey(uuid());
                        })
                );
            } catch (e) {
                toast.error(`${t("There's an error")}`, {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 2000,
                    theme: "colored",
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
            }
        }
        if (isSomeConsumed) {
            ErrorToast(
                t(
                    "Please note, you cannot archive an element used in a template"
                )
            );
            setIsLoading(false);
        }
        if (isFavorite) {
            ErrorToast(
                t("Please note, you cannot archive a favorite language")
            );
            setIsLoading(false);
        }
    }

    const getArrayOfRows = () => {
        let arrayOfRows: any[] = [];
        let ids: any[] = [];
        selectedRows.forEach(async (el: any) => {
            if (el[t("Shop")] !== undefined) {
                el.subRows.forEach((elt: any) => {
                    if (ids.indexOf(`${elt.shopId}${elt.Id}`) === -1) {
                        arrayOfRows.push(elt);
                        ids.push(`${elt.shopId}${elt.Id}`);
                    }
                });
            } else {
                if (ids.indexOf(`${el.shopId}${el.Id}`) === -1) {
                    arrayOfRows.push(el);
                    ids.push(`${el.shopId}${el.Id}`);
                }
            }
        });
        return arrayOfRows;
    };

    async function archiveShopLanguage() {
        setIsLoading(true);
        toast.dismiss();
        setLimit(3);
        let isSuccuss: any = [];
        let arrayOfRows = getArrayOfRows();
        let isSomeFavorite: any = false;
        let isSomeConsumed: any = false;
        for (let el of arrayOfRows) {
            const id = el.Id;
            if (el.isFavorite === true) {
                isSomeFavorite = true;
            } else {
                let isConsumed: any = await isLanguageConsumed(el.shopId, id);
                if (isConsumed === true) {
                    isSomeConsumed = true;
                } else {
                    let dataLanguage = {
                        userId: userID,
                        shopId: [el.shopId],
                        franchiseId: franchiseID,
                        operatorId: operatorID,
                        uuidUser: uuidUser,
                        isShop: true,
                        data: {
                            [id]: {
                                id: id,
                                language: languages.find(
                                    (lg: any) => lg.value === el.abbreviation
                                )?.label,
                                display_name: el.display_name,
                                country: el.country,
                                urlImage: el.url_image,
                                abbreviation: el.abbreviation,
                                isArchived: true,
                                shopId: el.shopId,
                                isActive: false,
                                isFavorite: el.isFavorite,
                                advancedDisplayName: el.advancedDisplayName,
                            },
                        },
                    };
                    if (
                        el.isActive === false &&
                        itemsDisable.find(
                            (item: any) =>
                                item.id === id && item.shopId === el.shopId
                        ) !== undefined
                    ) {
                        const element = itemsDisable.filter(
                            (item: any) =>
                                !(item.id === id && item.shopId === el.shopId)
                        );
                        setItemsDisable(element);
                    }
                    const data = new FormData();
                    data.append("dataLanguage", JSON.stringify(dataLanguage));
                    data.append("image", "");
                    const apiUrlAdd = `${process.env.REACT_APP_API_V2_URL}/settings/general/language`;
                    const requestOptions = {
                        method: "POST",
                        body: data,
                    };
                    try {
                        mutate(
                            apiUrlAdd,
                            await fetch(apiUrlAdd, requestOptions)
                                .then((response) => response.json())
                                .then((data) => {
                                    if (data.error === true) {
                                        setError(true);
                                    } else {
                                        isSuccuss.push(true);
                                    }
                                })
                        );
                    } catch (e) {
                        toast.error(`${t("There's an error")}`, {
                            position: toast.POSITION.TOP_CENTER,
                            autoClose: 2000,
                            theme: "colored",
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                        });
                        setError(true);
                        setIsLoading(false);
                    }
                }
            }
        }
        if (error === false && isSuccuss.length !== 0) {
            toast.success(`${t("The language was successfully archived")}`, {
                position: toast.POSITION.TOP_CENTER,
                theme: "colored",
                icon: <CheckboxIcon height={25} width={25} fill="white" />,
            });
            setKey(uuid());
            setIsLoading(false);
        }
        if (isSomeFavorite === true) {
            ErrorToast(
                t("Please note, you cannot archive a favorite language")
            );
            setIsLoading(false);
        }
        if (isSomeConsumed === true) {
            ErrorToast(
                t(
                    "Please note, you cannot archive an element used in a template"
                )
            );
            setIsLoading(false);
        }
    }

    return (
        <StyledModal
            toggle={true}
            isOpen={isValidateArchiveOpened || isValidateShopArchiveOpened}
            centered
        >
            <ModalHeader
                className="text-capitalize"
                toggle={() => {
                    setIsValidateArchiveOpened(false);
                    setIsValidateShopArchiveOpened(false);
                }}
            >
                <span>{capitalize(t("Alert"))}</span>
            </ModalHeader>
            <ModalBody>
                <StyledLabel
                    className="pl-3 m-0"
                    style={{ whiteSpace: "pre-line" }}
                >
                    {t(
                        "Attention, these language(s) can be used by other modules.\n Do you want to confirm the archiving of the selected language(s)"
                    )}
                </StyledLabel>
            </ModalBody>
            <ModalFooter className="border-top-0 ">
                <React.Fragment>
                    <CustomSecondaryColorButton
                        outline
                        variant="light"
                        onClick={() => {
                            setIsValidateArchiveOpened(false);
                            setIsValidateShopArchiveOpened(false);
                        }}
                        rounded
                    >
                        {t("Cancel")}
                    </CustomSecondaryColorButton>
                    <CustomMainColorButton
                        rounded
                        className="mr-2"
                        variant="primary"
                        onClick={() => {
                            // setLimit(2);
                            isValidateArchiveOpened
                                ? archiveFranchiseLanguage()
                                : archiveShopLanguage();
                            setIsValidateArchiveOpened(false);
                            setIsValidateShopArchiveOpened(false);
                        }}
                    >
                        {t("Validate")}
                    </CustomMainColorButton>
                </React.Fragment>
            </ModalFooter>
        </StyledModal>
    );
}
